import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { PageHeader } from "@/components/layout/PageHeader";
import { Timeline } from "@/components/timeline/Timeline";
import { ShipmentStepper } from "@/components/shipments/ShipmentStepper";
import { PickupIntakeForm } from "@/components/shipments/PickupIntakeForm";
import { toast } from "sonner";
import { formatDateLong, formatTimeRange } from "@/lib/format/date";
import {
  ArrowLeft,
  Package,
  MapPin,
  User,
  Phone,
  Calendar,
  Clock,
  ExternalLink,
  ShieldCheck,
  Loader2,
  Hand,
  Check,
  AlertCircle,
} from "lucide-react";

interface OrderItem {
  id: string;
  garmentType: string;
  qty: number;
  tagCode?: string | null;
  conditionNotes?: string | null;
  intakePhotos?: string[] | null;
  intakeNotes?: string | null;
  intakeConfirmedAt?: string | null;
  status: string;
}

interface Shipment {
  id: string;
  type: string;
  status: string;
  otp?: string | null;
  otpVerifiedAt?: string | null;
  otpVerifiedBy?: { id: string; name: string } | null;
  proofPhotos?: string[] | null;
  notes?: string | null;
  slot: {
    startAt: string;
    endAt: string;
  };
  order: {
    id: string;
    publicId: string;
    status: string;
    orderType: string;
    itemCount: number;
    items: OrderItem[];
  };
  customer: {
    publicId: string;
    name: string;
    phone?: string | null;
  };
  address: {
    line1: string;
    line2?: string | null;
    city: string;
    state: string;
    pincode: string;
    lat?: number | null;
    lng?: number | null;
  };
  area: {
    name: string;
  };
  assignedRider?: {
    id: string;
    name: string;
    phone?: string | null;
  } | null;
}

interface TimelineEntry {
  id: string;
  fromStatus: string | null;
  toStatus: string;
  actor?: { id: string; publicId: string; name: string; role: string } | null;
  meta?: Record<string, unknown> | null;
  createdAt: string;
}

const typeConfig: Record<string, { label: string; color: string }> = {
  PICKUP: { label: "Pickup", color: "bg-blue-100 text-blue-700" },
  DELIVERY: { label: "Delivery", color: "bg-green-100 text-green-700" },
  RETURN: { label: "Return", color: "bg-orange-100 text-orange-700" },
  REWORK: { label: "Rework", color: "bg-amber-100 text-amber-700" },
};

const statusConfig: Record<string, { color: string; label: string }> = {
  SCHEDULED: { color: "bg-gray-100 text-gray-700", label: "Scheduled" },
  ASSIGNED: { color: "bg-blue-100 text-blue-700", label: "Assigned" },
  EN_ROUTE: { color: "bg-cyan-100 text-cyan-700", label: "En Route" },
  ARRIVED: { color: "bg-teal-100 text-teal-700", label: "Arrived" },
  COLLECTED: { color: "bg-emerald-100 text-emerald-700", label: "Collected" },
  HANDED_TO_WORKSHOP: { color: "bg-green-100 text-green-700", label: "At Workshop" },
  OUT_FOR_DELIVERY: { color: "bg-orange-100 text-orange-700", label: "Out for Delivery" },
  DELIVERED: { color: "bg-green-100 text-green-700", label: "Delivered" },
  FAILED_ATTEMPT: { color: "bg-red-100 text-red-700", label: "Failed" },
};

export default function ShipmentDetailPage() {
  const navigate = useNavigate();
  const params = useParams();
  const shipmentId = params.shipmentId as string;

  const [loading, setLoading] = useState(true);
  const [shipment, setShipment] = useState<Shipment | null>(null);
  const [timeline, setTimeline] = useState<TimelineEntry[]>([]);

  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [isClaiming, setIsClaiming] = useState(false);
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
  const [isConfirmingIntake, setIsConfirmingIntake] = useState(false);

  const [otpInput, setOtpInput] = useState("");

  useEffect(() => {
    fetchShipment();
  }, [shipmentId]);

  async function fetchShipment() {
    setLoading(true);
    try {
      const response = await fetch(`/api/staff/shipments/${shipmentId}`);
      if (!response.ok) throw new Error("Failed to fetch shipment");

      const data = await response.json();
      setShipment(data.shipment);
      setTimeline(data.timeline || []);
    } catch (error) {
      toast.error("Failed to load shipment");
      navigate("/staff/shipments");
    } finally {
      setLoading(false);
    }
  }

  async function handleClaim() {
    setIsClaiming(true);
    try {
      const response = await fetch(`/api/staff/shipments/${shipmentId}/claim`, {
        method: "POST",
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to claim shipment");
      }

      toast.success("Shipment claimed successfully!");
      fetchShipment();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to claim");
    } finally {
      setIsClaiming(false);
    }
  }

  async function handleStatusChange(toStatus: string) {
    setIsUpdatingStatus(true);
    try {
      const response = await fetch(`/api/staff/shipments/${shipmentId}/status`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ toStatus }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to update status");
      }

      toast.success(`Status updated to ${toStatus}`);
      fetchShipment();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to update status");
    } finally {
      setIsUpdatingStatus(false);
    }
  }

  async function handleVerifyOtp() {
    if (otpInput.length !== 4) {
      toast.error("OTP must be 4 digits");
      return;
    }

    setIsVerifyingOtp(true);
    try {
      const response = await fetch(
        `/api/staff/shipments/${shipmentId}/otp/verify`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ otp: otpInput }),
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "OTP verification failed");
      }

      toast.success("OTP verified successfully!");
      setOtpInput("");
      fetchShipment();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "OTP verification failed");
    } finally {
      setIsVerifyingOtp(false);
    }
  }

  async function handleIntakeConfirm(items: any[]) {
    setIsConfirmingIntake(true);
    try {
      const response = await fetch(
        `/api/staff/shipments/${shipmentId}/pickup-intake/confirm`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ items }),
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to confirm intake");
      }

      toast.success("Intake confirmed successfully!");
      fetchShipment();
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Failed to confirm intake"
      );
    } finally {
      setIsConfirmingIntake(false);
    }
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <PageHeader title="Shipment Details" />
        <div className="grid gap-6 lg:grid-cols-3">
          <Skeleton className="h-[400px] lg:col-span-2" />
          <Skeleton className="h-[400px]" />
        </div>
      </div>
    );
  }

  if (!shipment) return null;

  const type = typeConfig[shipment.type] || typeConfig.PICKUP;
  const status = statusConfig[shipment.status] || statusConfig.SCHEDULED;
  const isUnassigned = !shipment.assignedRider;
  const isPickup = shipment.type === "PICKUP";
  const showIntakeForm =
    isPickup &&
    shipment.otpVerifiedAt &&
    ["ARRIVED", "COLLECTED"].includes(shipment.status);

  const mapsUrl =
    shipment.address.lat && shipment.address.lng
      ? `https://www.google.com/maps/search/?api=1&query=${shipment.address.lat},${shipment.address.lng}`
      : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
          `${shipment.address.line1}, ${shipment.address.city}, ${shipment.address.pincode}`
        )}`;

  return (
    <div className="space-y-6">
      <PageHeader
        title={`${type.label} - ${shipment.order.publicId}`}
        actions={
          <Button
            variant="outline"
            onClick={() => navigate("/staff/shipments")}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
        }
      />

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader className="pb-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Badge className={type.color}>{type.label}</Badge>
                  <Badge className={status.color}>{status.label}</Badge>
                  {shipment.otpVerifiedAt && (
                    <Badge className="bg-emerald-100 text-emerald-700 gap-1">
                      <ShieldCheck className="h-3 w-3" />
                      OTP Verified
                    </Badge>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="flex items-start gap-3">
                  <Calendar className="h-5 w-5 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-sm text-muted-foreground">Scheduled</p>
                    <p className="font-medium">
                      {formatDateLong(shipment.slot.startAt)}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {formatTimeRange(
                        shipment.slot.startAt,
                        shipment.slot.endAt
                      )}
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <Package className="h-5 w-5 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-sm text-muted-foreground">Order</p>
                    <p className="font-medium">{shipment.order.publicId}</p>
                    <p className="text-sm text-muted-foreground">
                      {shipment.order.itemCount} items -{" "}
                      {shipment.order.orderType}
                    </p>
                  </div>
                </div>
              </div>

              <Separator />

              <div className="flex items-start gap-3">
                <User className="h-5 w-5 text-muted-foreground mt-0.5" />
                <div>
                  <p className="text-sm text-muted-foreground">Customer</p>
                  <p className="font-medium">{shipment.customer.name}</p>
                  <p className="text-sm text-muted-foreground">
                    {shipment.customer.publicId}
                  </p>
                  {shipment.customer.phone && (
                    <a
                      href={`tel:${shipment.customer.phone}`}
                      className="flex items-center gap-1 text-primary text-sm mt-1 hover:underline"
                    >
                      <Phone className="h-3 w-3" />
                      {shipment.customer.phone}
                    </a>
                  )}
                </div>
              </div>

              <Separator />

              <div className="flex items-start gap-3">
                <MapPin className="h-5 w-5 text-muted-foreground mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm text-muted-foreground">Address</p>
                  <p className="font-medium">{shipment.address.line1}</p>
                  {shipment.address.line2 && (
                    <p className="text-sm">{shipment.address.line2}</p>
                  )}
                  <p className="text-sm text-muted-foreground">
                    {shipment.address.city}, {shipment.address.state} -{" "}
                    {shipment.address.pincode}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Area: {shipment.area.name}
                  </p>
                  <a
                    href={mapsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-primary text-sm mt-2 hover:underline"
                  >
                    <ExternalLink className="h-3 w-3" />
                    Open in Maps
                  </a>
                </div>
              </div>

              {isUnassigned && (
                <>
                  <Separator />
                  <Button
                    onClick={handleClaim}
                    disabled={isClaiming}
                    className="w-full"
                  >
                    {isClaiming ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Claiming...
                      </>
                    ) : (
                      <>
                        <Hand className="h-4 w-4 mr-2" />
                        Claim This Shipment
                      </>
                    )}
                  </Button>
                </>
              )}
            </CardContent>
          </Card>

          {!isUnassigned && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Status Progression</CardTitle>
              </CardHeader>
              <CardContent>
                <ShipmentStepper
                  type={shipment.type}
                  status={shipment.status}
                  otpVerified={!!shipment.otpVerifiedAt}
                  onStatusChange={handleStatusChange}
                  isUpdating={isUpdatingStatus}
                />
              </CardContent>
            </Card>
          )}

          {!isUnassigned && !shipment.otpVerifiedAt && shipment.otp && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <ShieldCheck className="h-4 w-4" />
                  OTP Verification
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <AlertCircle className="h-5 w-5 text-amber-600 shrink-0" />
                  <p className="text-sm text-amber-700">
                    Ask the customer for their OTP to verify this{" "}
                    {shipment.type.toLowerCase()}.
                  </p>
                </div>

                <div className="flex items-end gap-3">
                  <div className="flex-1 space-y-2">
                    <Label htmlFor="otp">Enter Customer OTP</Label>
                    <Input
                      id="otp"
                      placeholder="0000"
                      value={otpInput}
                      onChange={(e) =>
                        setOtpInput(e.target.value.replace(/\D/g, "").slice(0, 4))
                      }
                      className="text-2xl tracking-widest text-center font-mono"
                      maxLength={4}
                    />
                  </div>
                  <Button
                    onClick={handleVerifyOtp}
                    disabled={otpInput.length !== 4 || isVerifyingOtp}
                  >
                    {isVerifyingOtp ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      "Verify"
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {shipment.otpVerifiedAt && (
            <Card className="border-green-200 bg-green-50">
              <CardContent className="py-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-green-100 rounded-full">
                    <Check className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <p className="font-medium text-green-800">OTP Verified</p>
                    <p className="text-sm text-green-600">
                      Verified at{" "}
                      {formatDateLong(shipment.otpVerifiedAt)}
                      {shipment.otpVerifiedBy &&
                        ` by ${shipment.otpVerifiedBy.name}`}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {showIntakeForm && (
            <PickupIntakeForm
              items={shipment.order.items}
              onConfirm={handleIntakeConfirm}
              isSubmitting={isConfirmingIntake}
            />
          )}
        </div>

        <div>
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Timeline</CardTitle>
            </CardHeader>
            <CardContent>
              <Timeline entries={timeline} />
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
